/**
 * CV Builder — /cv-builder
 *
 * Four entry paths:
 *   1. Blank template      → just scroll to editor
 *   2. Upload a file       → POST /api/cv/extract  → /cv/parse
 *   3. Take a photo        → POST /api/cv/extract/image → /cv/parse
 *   4. Add profile photo   → POST /api/cv/photo
 *
 * Editor is a 2-column layout on desktop (form left, live preview right) and
 * stacked with a preview toggle on mobile. All AI actions hit /api/cv/ai.
 *
 * Drafts auto-persist (debounced) to localStorage AND /api/cv/draft using a
 * client_id cookie-like token when the user is anonymous.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, UploadCloud, Camera, Image as ImageIcon, Loader2, Plus, Trash2,
  Sparkles, Languages, Download, Save, Eye, Pencil,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import { GlobalNav } from '@/components/GlobalNav';
import { CVScorePanel } from '@/components/CVScorePanel';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;
const DRAFT_LS_KEY = 'keikka_cv_draft_v1';
const CLIENT_ID_KEY = 'keikka_cv_client_id';

const EMPTY_DRAFT = {
  first_name: '', last_name: '', job_title: '', phone: '', email: '',
  location: '', country: '', date_of_birth: '', driving_license: '',
  own_car: false, work_abroad: false, shift_work: false, summary: '',
  experience: [], education: [], skills: [], languages: [],
  certificates: [], hobbies: '', references: [], photo_id: '',
  photo_url: '', template: 'classic', ui_language: 'et', include_photo: true,
};

function getOrCreateClientId() {
  try {
    let v = localStorage.getItem(CLIENT_ID_KEY);
    if (!v) {
      v = 'cv-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(CLIENT_ID_KEY, v);
    }
    return v;
  } catch (_) {
    return 'cv-anon';
  }
}

const TRANSLATE_LANGS = [
  { code: 'et', label: 'Eesti' },
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Suomi' },
  { code: 'nb', label: 'Norsk' },
  { code: 'ru', label: 'Русский' },
];

/* =========================================================================
 * Entry cards
 * =======================================================================*/
const EntryCards = ({ t, onBlank, onUploadFile, onUploadImage, onUploadPhoto, busy }) => {
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const profileRef = useRef(null);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="entry-cards">
      <button
        type="button"
        onClick={onBlank}
        disabled={busy}
        data-testid="entry-blank"
        className="group text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-orange-300 hover:shadow-sm transition disabled:opacity-60"
      >
        <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
          <FileText className="h-5 w-5" />
        </div>
        <div className="font-semibold text-slate-900">{t('cvBuilder.entryBlankTitle')}</div>
        <p className="text-sm text-slate-600 mt-1">{t('cvBuilder.entryBlankDesc')}</p>
      </button>

      <label
        htmlFor="cv-file-input"
        data-testid="entry-upload"
        className={`group text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-orange-300 hover:shadow-sm transition cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
          <UploadCloud className="h-5 w-5" />
        </div>
        <div className="font-semibold text-slate-900">{t('cvBuilder.entryUploadTitle')}</div>
        <p className="text-sm text-slate-600 mt-1">{t('cvBuilder.entryUploadDesc')}</p>
        <input
          id="cv-file-input"
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onUploadFile(e.target.files[0])}
          data-testid="entry-upload-input"
        />
      </label>

      <label
        htmlFor="cv-photo-input"
        data-testid="entry-photo"
        className={`group text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-orange-300 hover:shadow-sm transition cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
          <Camera className="h-5 w-5" />
        </div>
        <div className="font-semibold text-slate-900">{t('cvBuilder.entryPhotoTitle')}</div>
        <p className="text-sm text-slate-600 mt-1">{t('cvBuilder.entryPhotoDesc')}</p>
        <input
          id="cv-photo-input"
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onUploadImage(e.target.files[0])}
          data-testid="entry-photo-input"
        />
      </label>

      <label
        htmlFor="cv-profile-input"
        data-testid="entry-profile"
        className={`group text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-orange-300 hover:shadow-sm transition cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
          <ImageIcon className="h-5 w-5" />
        </div>
        <div className="font-semibold text-slate-900">{t('cvBuilder.entryProfileTitle')}</div>
        <p className="text-sm text-slate-600 mt-1">{t('cvBuilder.entryProfileDesc')}</p>
        <input
          id="cv-profile-input"
          ref={profileRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onUploadPhoto(e.target.files[0])}
          data-testid="entry-profile-input"
        />
      </label>
    </div>
  );
};

/* =========================================================================
 * Live preview — the "polished CV" that gets exported to PDF
 * =======================================================================*/
const Preview = React.forwardRef(({ draft, t }, ref) => {
  const fullName = [draft.first_name, draft.last_name].filter(Boolean).join(' ');
  const photoSrc = draft.photo_url ? `${API}${draft.photo_url}` : '';
  // Normalise legacy template ids (simple→classic, professional→compact)
  const template = ({ simple: 'classic', professional: 'compact' }[draft.template]) || draft.template || 'classic';

  const borderAccent = template === 'modern'
    ? 'border-l-4 border-orange-500 pl-3'
    : template === 'compact'
      ? 'border-b border-slate-300 pb-0.5'
      : 'border-b border-slate-200 pb-1';

  const hasAny =
    fullName || draft.job_title || draft.phone || draft.email ||
    draft.summary || draft.experience.length || draft.education.length ||
    draft.skills.length || draft.languages.length ||
    draft.certificates.length || draft.hobbies;

  if (!hasAny) {
    return (
      <div
        className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500 min-h-[300px] flex items-center justify-center text-center"
        data-testid="preview-empty"
      >
        {t('cvBuilder.previewEmpty')}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      id="cv-print-surface"
      data-testid="cv-preview"
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 text-slate-900 space-y-5"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      {/* Header */}
      <header className="flex items-start gap-4">
        {draft.include_photo && photoSrc ? (
          <img
            src={photoSrc}
            alt=""
            className="w-20 h-20 rounded-full object-cover border border-slate-200"
            crossOrigin="anonymous"
          />
        ) : null}
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
            {fullName || '—'}
          </h1>
          {draft.job_title && (
            <div className="text-base text-slate-600 mt-0.5">{draft.job_title}</div>
          )}
          <div className="text-sm text-slate-600 mt-2 space-x-2">
            {draft.email && <span>{draft.email}</span>}
            {draft.phone && <span>· {draft.phone}</span>}
            {draft.location && <span>· {draft.location}</span>}
            {draft.country && <span>· {draft.country}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1 space-x-2">
            {draft.driving_license && <span>{t('cvBuilder.drivingLicense')}: {draft.driving_license}</span>}
            {draft.own_car && <span>· {t('cvBuilder.ownCar')}</span>}
            {draft.work_abroad && <span>· {t('cvBuilder.workAbroad')}</span>}
            {draft.shift_work && <span>· {t('cvBuilder.shiftWork')}</span>}
          </div>
        </div>
      </header>

      {draft.summary && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionSummary')}
          </h2>
          <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{draft.summary}</p>
        </section>
      )}

      {draft.experience.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionExperience')}
          </h2>
          <div className="mt-2 space-y-3">
            {draft.experience.map((e, i) => (
              <div key={i}>
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-semibold text-slate-900">{e.position || '—'}</span>
                  <span className="text-slate-500">
                    {[e.start_date, e.end_date].filter(Boolean).join(' – ')}
                  </span>
                </div>
                <div className="text-sm text-slate-700">{e.company}</div>
                {e.description && (
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{e.description}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {draft.education.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionEducation')}
          </h2>
          <div className="mt-2 space-y-3">
            {draft.education.map((e, i) => (
              <div key={i}>
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-semibold text-slate-900">{e.school || '—'}</span>
                  <span className="text-slate-500">
                    {[e.start_date, e.end_date].filter(Boolean).join(' – ')}
                  </span>
                </div>
                {e.field && <div className="text-sm text-slate-700">{e.field}</div>}
                {e.notes && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{e.notes}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {draft.skills.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionSkills')}
          </h2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {draft.skills.map((s, i) => (
              <span key={i} className="inline-flex text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-800">
                {s}
              </span>
            ))}
          </div>
        </section>
      )}

      {draft.languages.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionLanguages')}
          </h2>
          <div className="text-sm text-slate-800 mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {draft.languages.map((l, i) => (
              <div key={i}>
                <span className="font-medium">{l.language}</span>
                {l.level && <span className="text-slate-500"> — {l.level}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {draft.certificates.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionCertificates')}
          </h2>
          <div className="mt-2 space-y-2">
            {draft.certificates.map((c, i) => (
              <div key={i} className="text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-slate-500">{c.date}</span>
                </div>
                <div className="text-slate-600">{c.issuer}</div>
                {c.notes && <div className="text-slate-600 text-xs mt-0.5">{c.notes}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {draft.hobbies && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionHobbies')}
          </h2>
          <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{draft.hobbies}</p>
        </section>
      )}

      {draft.references.length > 0 && (
        <section>
          <h2 className={`text-sm font-bold uppercase tracking-wide text-slate-700 ${borderAccent}`}>
            {t('cvBuilder.sectionReferences')}
          </h2>
          <div className="mt-2 space-y-2">
            {draft.references.map((r, i) => (
              <div key={i} className="text-sm">
                <div className="font-medium">{r.name}{r.position && <span className="font-normal text-slate-600"> — {r.position}</span>}</div>
                <div className="text-slate-600">{r.contact}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
});

/* =========================================================================
 * Editor
 * =======================================================================*/
const AIButton = ({ label, onClick, busy, testId }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={busy}
    className="text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
    data-testid={testId}
  >
    {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
    {label}
  </Button>
);

const SectionHeader = ({ title, testId, children }) => (
  <div className="flex items-center justify-between mb-2" data-testid={testId}>
    <h3 className="text-sm font-bold text-slate-900">{title}</h3>
    <div className="flex items-center gap-1">{children}</div>
  </div>
);

const CVBuilderPage = () => {
  const { t, language } = useLanguage();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState('');
  const [showPreviewMobile, setShowPreviewMobile] = useState(false);
  const clientId = useMemo(getOrCreateClientId, []);
  const previewRef = useRef(null);

  // Load draft on mount (localStorage first, server second)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_LS_KEY);
      if (raw) setDraft({ ...EMPTY_DRAFT, ...JSON.parse(raw) });
    } catch (_) {}
    (async () => {
      try {
        const r = await axios.get(`${API}/api/cv/draft`, { params: { client_id: clientId } });
        const d = r?.data?.draft;
        if (d) setDraft((prev) => ({ ...EMPTY_DRAFT, ...d, ...prev }));
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced save to localStorage + server
  useEffect(() => {
    const handle = setTimeout(() => {
      try { localStorage.setItem(DRAFT_LS_KEY, JSON.stringify(draft)); } catch (_) {}
    }, 400);
    return () => clearTimeout(handle);
  }, [draft]);

  const patch = useCallback((p) => setDraft((d) => ({ ...d, ...p })), []);
  const update = useCallback((key, value) => setDraft((d) => ({ ...d, [key]: value })), []);
  const updateIdx = useCallback((key, i, item) => setDraft((d) => {
    const list = [...d[key]];
    list[i] = { ...list[i], ...item };
    return { ...d, [key]: list };
  }), []);
  const addItem = useCallback((key, blank) => setDraft((d) => ({ ...d, [key]: [...d[key], blank] })), []);
  const removeItem = useCallback((key, i) => setDraft((d) => {
    const list = [...d[key]];
    list.splice(i, 1);
    return { ...d, [key]: list };
  }), []);

  const applyParsed = (parsed) => {
    setDraft((d) => ({
      ...d,
      first_name: parsed.first_name || d.first_name,
      last_name: parsed.last_name || d.last_name,
      job_title: parsed.job_title || d.job_title,
      phone: parsed.phone || d.phone,
      email: parsed.email || d.email,
      location: parsed.location || d.location,
      country: parsed.country || d.country,
      date_of_birth: parsed.date_of_birth || d.date_of_birth,
      driving_license: parsed.driving_license || d.driving_license,
      summary: parsed.summary || d.summary,
      experience: parsed.experience?.length ? parsed.experience : d.experience,
      education: parsed.education?.length ? parsed.education : d.education,
      skills: parsed.skills?.length ? parsed.skills : d.skills,
      languages: parsed.languages?.length ? parsed.languages : d.languages,
      certificates: parsed.certificates?.length ? parsed.certificates : d.certificates,
      hobbies: parsed.hobbies || d.hobbies,
      references: parsed.references?.length ? parsed.references : d.references,
    }));
  };

  const extractAndParse = async (endpoint, file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const extract = await axios.post(`${API}/api/cv/${endpoint}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = extract?.data?.text || '';
      if (!text.trim()) {
        toast.error(t('cvBuilder.extractFailed'));
        return;
      }
      const parsed = await axios.post(`${API}/api/cv/parse`, { text });
      applyParsed(parsed.data || {});
      toast.success(t('cvBuilder.extractedOk'));
    } catch (e) {
      toast.error(`${t('cvBuilder.extractFailed')} (${e?.response?.status || ''})`);
    } finally {
      setBusy(false);
    }
  };

  const uploadProfilePhoto = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/cv/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      patch({ photo_id: r.data.photo_id, photo_url: r.data.url });
      toast.success(t('cvBuilder.extractedOk'));
    } catch (e) {
      toast.error(t('cvBuilder.extractFailed'));
    } finally {
      setBusy(false);
    }
  };

  const ai = async (action, textField, options = {}) => {
    const current = textField === 'skills' ? draft.skills.join(', ') : (draft[textField] || '');
    if (action !== 'summary' && !current.trim()) return;
    setAiBusy(`${action}:${textField}`);
    try {
      const body = {
        action,
        text: action === 'summary'
          ? JSON.stringify({
              job_title: draft.job_title,
              experience: draft.experience,
              education: draft.education,
              skills: draft.skills,
            })
          : current,
        target_language: options.target_language,
      };
      const r = await axios.post(`${API}/api/cv/ai`, body);
      const out = (r.data?.text || '').trim();
      if (!out) return;
      if (textField === 'skills' && action === 'skills') {
        update('skills', out.split(',').map((s) => s.trim()).filter(Boolean));
      } else if (action === 'summary') {
        update('summary', out);
      } else {
        update(textField, out);
      }
      toast.success('AI ✓');
    } catch (e) {
      toast.error('AI error');
    } finally {
      setAiBusy('');
    }
  };

  const translateAll = async (targetLang) => {
    setAiBusy(`translate:${targetLang}`);
    try {
      // Translate the big free-text fields in parallel
      const fields = ['summary', 'hobbies'];
      await Promise.all(fields.map(async (f) => {
        const v = draft[f];
        if (!v?.trim()) return;
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'translate',
          text: v,
          target_language: targetLang,
        });
        update(f, (r.data?.text || '').trim());
      }));
      // Translate skills list (comma-separated roundtrip)
      if (draft.skills.length) {
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'translate',
          text: draft.skills.join(', '),
          target_language: targetLang,
        });
        update('skills', (r.data?.text || '').split(',').map((s) => s.trim()).filter(Boolean));
      }
      toast.success('✓');
    } catch (_) {
      toast.error('Translate error');
    } finally {
      setAiBusy('');
    }
  };

  const saveDraft = async () => {
    try {
      await axios.post(`${API}/api/cv/draft`, {
        client_id: clientId,
        draft: {
          ...draft,
          // server expects only draft fields (photo_url is frontend-only)
        },
      });
      toast.success(t('cvBuilder.draftSaved'));
    } catch (e) {
      toast.error('Save error');
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      // 1) Try server-side reportlab PDF (precise, multi-template, locale-aware)
      const r = await axios.post(
        `${API}/api/cv/pdf`,
        {
          draft: { ...draft, ui_language: language || draft.ui_language || 'en' },
          template: draft.template || 'classic',
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = [draft.first_name, draft.last_name].filter(Boolean).join('_') || 'cv';
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('cvBuilder.draftSaved'));
      return;
    } catch (_) {
      // 2) Fallback: client-side html2pdf from the live preview
      try {
        const mod = await import('html2pdf.js');
        const html2pdf = mod.default || mod;
        const element = previewRef.current;
        if (!element) return;
        await html2pdf().set({
          margin: 0.4,
          filename: `${[draft.first_name, draft.last_name].filter(Boolean).join('_') || 'cv'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        }).from(element).save();
      } catch (e) {
        toast.error('PDF error');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="cv-builder-page">
      <GlobalNav />

      <main className="flex-1 bg-slate-50/50">
        <section className="bg-white border-b border-slate-100">
          <div className="max-w-[1200px] mx-auto px-4 pt-8 pb-5">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="cv-hero-title">
              {t('cvBuilder.pageTitle')}
            </h1>
            <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl" data-testid="cv-hero-sub">
              {t('cvBuilder.pageSub')}
            </p>
            <div className="mt-5">
              <EntryCards
                t={t}
                busy={busy}
                onBlank={() => {
                  const el = document.getElementById('cv-editor-anchor');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onUploadFile={(f) => extractAndParse('extract', f)}
                onUploadImage={(f) => extractAndParse('extract/image', f)}
                onUploadPhoto={uploadProfilePhoto}
              />
            </div>
          </div>
        </section>

        {/* TOOLBAR */}
        <section id="cv-editor-anchor" className="bg-white border-b border-slate-100 sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-white/90">
          <div className="max-w-[1200px] mx-auto px-4 py-2 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Select value={draft.template} onValueChange={(v) => patch({ template: v })}>
                <SelectTrigger className="h-9 w-40" data-testid="tpl-select">
                  <SelectValue placeholder={t('cvBuilder.template')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="classic">{t('cvBuilder.tplClassic')}</SelectItem>
                  <SelectItem value="modern">{t('cvBuilder.tplModern')}</SelectItem>
                  <SelectItem value="compact">{t('cvBuilder.tplCompact')}</SelectItem>
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9" data-testid="translate-menu">
                    <Languages className="h-4 w-4 mr-1" />
                    {t('cvBuilder.aiTranslate')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>{t('cvBuilder.aiTranslate')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {TRANSLATE_LANGS.map((l) => (
                    <DropdownMenuItem
                      key={l.code}
                      onClick={() => translateAll(l.code)}
                      data-testid={`translate-${l.code}`}
                    >
                      {l.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9 md:hidden"
                onClick={() => setShowPreviewMobile((v) => !v)}
                data-testid="toggle-preview-mobile"
              >
                {showPreviewMobile
                  ? <><Pencil className="h-4 w-4 mr-1" />{t('cvBuilder.sectionPersonal')}</>
                  : <><Eye className="h-4 w-4 mr-1" />{t('cvBuilder.preview')}</>
                }
              </Button>
              <Button variant="outline" size="sm" className="h-9" onClick={saveDraft} data-testid="save-draft">
                <Save className="h-4 w-4 mr-1" />
                {t('cvBuilder.saveDraft')}
              </Button>
              <Button
                size="sm"
                className="h-9 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={downloadPdf}
                data-testid="download-pdf"
              >
                <Download className="h-4 w-4 mr-1" />
                {t('cvBuilder.downloadPdf')}
              </Button>
            </div>
          </div>
        </section>

        <section className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="grid md:grid-cols-12 gap-6">
            {/* Form */}
            <div className={`md:col-span-7 ${showPreviewMobile ? 'hidden md:block' : ''}`}>
              <div className="space-y-5">
                {/* Personal */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionPersonal')} testId="section-personal" />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder={t('cvBuilder.firstName')} value={draft.first_name}
                      onChange={(e) => update('first_name', e.target.value)} data-testid="f-firstname" />
                    <Input placeholder={t('cvBuilder.lastName')} value={draft.last_name}
                      onChange={(e) => update('last_name', e.target.value)} data-testid="f-lastname" />
                    <Input className="sm:col-span-2" placeholder={t('cvBuilder.jobTitle')} value={draft.job_title}
                      onChange={(e) => update('job_title', e.target.value)} data-testid="f-jobtitle" />
                    <Input placeholder={t('cvBuilder.phone')} value={draft.phone}
                      onChange={(e) => update('phone', e.target.value)} data-testid="f-phone" />
                    <Input placeholder={t('cvBuilder.email')} value={draft.email}
                      onChange={(e) => update('email', e.target.value)} data-testid="f-email" />
                    <Input placeholder={t('cvBuilder.location')} value={draft.location}
                      onChange={(e) => update('location', e.target.value)} data-testid="f-location" />
                    <Input placeholder={t('cvBuilder.country')} value={draft.country}
                      onChange={(e) => update('country', e.target.value)} data-testid="f-country" />
                    <Input placeholder={t('cvBuilder.dob')} value={draft.date_of_birth}
                      onChange={(e) => update('date_of_birth', e.target.value)} data-testid="f-dob" />
                    <Input placeholder={t('cvBuilder.drivingLicense')} value={draft.driving_license}
                      onChange={(e) => update('driving_license', e.target.value)} data-testid="f-license" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                    <label className="flex items-center gap-2 text-sm">
                      <Switch checked={draft.own_car} onCheckedChange={(v) => update('own_car', v)} data-testid="f-owncar" />
                      {t('cvBuilder.ownCar')}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Switch checked={draft.work_abroad} onCheckedChange={(v) => update('work_abroad', v)} data-testid="f-abroad" />
                      {t('cvBuilder.workAbroad')}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Switch checked={draft.shift_work} onCheckedChange={(v) => update('shift_work', v)} data-testid="f-shift" />
                      {t('cvBuilder.shiftWork')}
                    </label>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionSummary')} testId="section-summary">
                    <AIButton label={t('cvBuilder.aiSummary')} onClick={() => ai('summary', 'summary')}
                      busy={aiBusy.startsWith('summary')} testId="ai-summary" />
                    <AIButton label={t('cvBuilder.aiImprove')} onClick={() => ai('improve', 'summary')}
                      busy={aiBusy === 'improve:summary'} testId="ai-improve-summary" />
                  </SectionHeader>
                  <Textarea
                    rows={5}
                    placeholder={t('cvBuilder.summaryPlaceholder')}
                    value={draft.summary}
                    onChange={(e) => update('summary', e.target.value)}
                    data-testid="f-summary"
                  />
                </div>

                {/* Experience */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionExperience')} testId="section-experience" />
                  <div className="space-y-3">
                    {draft.experience.map((exp, i) => (
                      <div key={i} className="grid sm:grid-cols-2 gap-2 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0" data-testid={`exp-row-${i}`}>
                        <Input placeholder={t('cvBuilder.company')} value={exp.company}
                          onChange={(e) => updateIdx('experience', i, { company: e.target.value })} />
                        <Input placeholder={t('cvBuilder.position')} value={exp.position}
                          onChange={(e) => updateIdx('experience', i, { position: e.target.value })} />
                        <Input placeholder={t('cvBuilder.startDate')} value={exp.start_date}
                          onChange={(e) => updateIdx('experience', i, { start_date: e.target.value })} />
                        <Input placeholder={t('cvBuilder.endDate')} value={exp.end_date}
                          onChange={(e) => updateIdx('experience', i, { end_date: e.target.value })} />
                        <Textarea className="sm:col-span-2" rows={3} placeholder={t('cvBuilder.description')}
                          value={exp.description}
                          onChange={(e) => updateIdx('experience', i, { description: e.target.value })} />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeItem('experience', i)}
                            className="text-slate-500">
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {t('cvBuilder.remove')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => addItem('experience', { company: '', position: '', start_date: '', end_date: '', description: '' })}
                    data-testid="add-experience">
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('cvBuilder.addExperience')}
                  </Button>
                </div>

                {/* Education */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionEducation')} testId="section-education" />
                  <div className="space-y-3">
                    {draft.education.map((ed, i) => (
                      <div key={i} className="grid sm:grid-cols-2 gap-2 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
                        <Input placeholder={t('cvBuilder.school')} value={ed.school}
                          onChange={(e) => updateIdx('education', i, { school: e.target.value })} />
                        <Input placeholder={t('cvBuilder.field')} value={ed.field}
                          onChange={(e) => updateIdx('education', i, { field: e.target.value })} />
                        <Input placeholder={t('cvBuilder.startDate')} value={ed.start_date}
                          onChange={(e) => updateIdx('education', i, { start_date: e.target.value })} />
                        <Input placeholder={t('cvBuilder.endDate')} value={ed.end_date}
                          onChange={(e) => updateIdx('education', i, { end_date: e.target.value })} />
                        <Textarea className="sm:col-span-2" rows={2} placeholder={t('cvBuilder.notes')}
                          value={ed.notes}
                          onChange={(e) => updateIdx('education', i, { notes: e.target.value })} />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeItem('education', i)}
                            className="text-slate-500">
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {t('cvBuilder.remove')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => addItem('education', { school: '', field: '', start_date: '', end_date: '', notes: '' })}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('cvBuilder.addEducation')}
                  </Button>
                </div>

                {/* Skills */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionSkills')} testId="section-skills">
                    <AIButton label={t('cvBuilder.aiSkills')} onClick={() => ai('skills', 'skills')}
                      busy={aiBusy === 'skills:skills'} testId="ai-skills" />
                  </SectionHeader>
                  <Textarea
                    rows={3}
                    placeholder={t('cvBuilder.skillsHint')}
                    value={draft.skills.join(', ')}
                    onChange={(e) => update('skills', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    data-testid="f-skills"
                  />
                </div>

                {/* Languages */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionLanguages')} testId="section-languages" />
                  <div className="space-y-2">
                    {draft.languages.map((l, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                        <Input className="sm:col-span-2" placeholder={t('cvBuilder.language')} value={l.language}
                          onChange={(e) => updateIdx('languages', i, { language: e.target.value })} />
                        <Input className="sm:col-span-2" placeholder={t('cvBuilder.level')} value={l.level}
                          onChange={(e) => updateIdx('languages', i, { level: e.target.value })} />
                        <Button variant="ghost" size="sm" onClick={() => removeItem('languages', i)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => addItem('languages', { language: '', level: '' })}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('cvBuilder.addLang')}
                  </Button>
                </div>

                {/* Certificates */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionCertificates')} testId="section-certificates" />
                  <div className="space-y-3">
                    {draft.certificates.map((c, i) => (
                      <div key={i} className="grid sm:grid-cols-2 gap-2 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
                        <Input placeholder={t('cvBuilder.certName')} value={c.name}
                          onChange={(e) => updateIdx('certificates', i, { name: e.target.value })} />
                        <Input placeholder={t('cvBuilder.issuer')} value={c.issuer}
                          onChange={(e) => updateIdx('certificates', i, { issuer: e.target.value })} />
                        <Input placeholder={t('cvBuilder.date')} value={c.date}
                          onChange={(e) => updateIdx('certificates', i, { date: e.target.value })} />
                        <Input placeholder={t('cvBuilder.notes')} value={c.notes}
                          onChange={(e) => updateIdx('certificates', i, { notes: e.target.value })} />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeItem('certificates', i)}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {t('cvBuilder.remove')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => addItem('certificates', { name: '', issuer: '', date: '', notes: '' })}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('cvBuilder.addCert')}
                  </Button>
                </div>

                {/* Hobbies */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionHobbies')} testId="section-hobbies" />
                  <Textarea
                    rows={3}
                    placeholder={t('cvBuilder.hobbiesHint')}
                    value={draft.hobbies}
                    onChange={(e) => update('hobbies', e.target.value)}
                    data-testid="f-hobbies"
                  />
                </div>

                {/* References */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionReferences')} testId="section-references" />
                  <div className="space-y-3">
                    {draft.references.map((r, i) => (
                      <div key={i} className="grid sm:grid-cols-2 gap-2 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
                        <Input placeholder={t('cvBuilder.refName')} value={r.name}
                          onChange={(e) => updateIdx('references', i, { name: e.target.value })} />
                        <Input placeholder={t('cvBuilder.position')} value={r.position}
                          onChange={(e) => updateIdx('references', i, { position: e.target.value })} />
                        <Input className="sm:col-span-2" placeholder={t('cvBuilder.refContact')} value={r.contact}
                          onChange={(e) => updateIdx('references', i, { contact: e.target.value })} />
                        <Input className="sm:col-span-2" placeholder={t('cvBuilder.notes')} value={r.notes}
                          onChange={(e) => updateIdx('references', i, { notes: e.target.value })} />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeItem('references', i)}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {t('cvBuilder.remove')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => addItem('references', { name: '', position: '', contact: '', notes: '' })}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('cvBuilder.addRef')}
                  </Button>
                </div>

                {/* Photo controls + Keikka profile opt-in */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <SectionHeader title={t('cvBuilder.sectionPhoto')} testId="section-photo" />
                  <div className="flex items-center gap-4">
                    {draft.photo_url ? (
                      <img
                        src={`${API}${draft.photo_url}`}
                        alt=""
                        className="w-20 h-20 rounded-full object-cover border border-slate-200"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-100 border border-slate-200" />
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <label htmlFor="cv-profile-upload">
                        <Button variant="outline" size="sm" asChild>
                          <span>
                            <UploadCloud className="h-3.5 w-3.5 mr-1" />
                            {draft.photo_url ? t('cvBuilder.replacePhoto') : t('cvBuilder.uploadPhoto')}
                          </span>
                        </Button>
                      </label>
                      <input
                        id="cv-profile-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadProfilePhoto(e.target.files[0])}
                      />
                      {draft.photo_url && (
                        <Button variant="ghost" size="sm"
                          onClick={() => patch({ photo_id: '', photo_url: '' })}
                          data-testid="remove-photo">
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {t('cvBuilder.removePhoto')}
                        </Button>
                      )}
                      <label className="flex items-center gap-2 text-sm ml-2">
                        <Switch checked={draft.include_photo} onCheckedChange={(v) => update('include_photo', v)} />
                        <span className="text-slate-600">{t('cvBuilder.sectionPhoto')}</span>
                      </label>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm mt-4 pt-4 border-t border-slate-100">
                    <Switch
                      checked={!!draft.use_for_keikka}
                      onCheckedChange={(v) => update('use_for_keikka', v)}
                      data-testid="use-for-keikka"
                    />
                    <span className="text-slate-700">{t('cvBuilder.useForKeikka')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Preview */}
            <aside className={`md:col-span-5 ${showPreviewMobile ? '' : 'hidden md:block'}`}>
              <div className="md:sticky md:top-16 space-y-4">
                <Preview draft={draft} t={t} ref={previewRef} />
                <CVScorePanel draft={draft} setDraft={setDraft} clientId={clientId} />
              </div>
            </aside>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white border-t border-slate-100 py-10">
          <div className="max-w-[1200px] mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-900 text-center mb-6">
              {t('cvBuilder.howTitle')}
            </h2>
            <ol className="grid md:grid-cols-3 gap-3">
              {[
                t('cvBuilder.howStep1'),
                t('cvBuilder.howStep2'),
                t('cvBuilder.howStep3'),
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-lg bg-orange-500 text-white flex items-center justify-center font-bold">
                      {i + 1}
                    </div>
                  </div>
                  <div className="text-sm text-slate-800 leading-snug pt-1.5">{step}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CVBuilderPage;
export { CVBuilderPage };
